<template>
  <div class="meeting-report-page">
    <SubPageHeader title="会议纪要" />
    <div class="content">
      <div v-if="isLoading" class="state-text">加载中...</div>
      <div v-else-if="!reportData" class="state-text">暂无会议纪要</div>
      <div v-else class="report-card">
        <!-- <div class="report-title">会议纪要</div> -->
        <div class="report-text" v-html="reportData.html"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMeetingReport } from '@/api';
import SubPageHeader from '../component/SubPageHeader.vue';

export default {
  name: 'MeetingReportDetailPage',
  components: {
    SubPageHeader
  },
  data() {
    return {
      isLoading: false,
      reportData: null
    };
  },
  mounted() {
    this.fetchMeetingReport();
  },
  methods: {
    getMeetingId() {
      const query = this.$route.query || {};
      return query.meetingId || query.id || query.aid || '';
    },
    normalizeReport(payload) {
      const text = this.getReportText(payload);
      if (!text) {
        return null;
      }
      return {
        text,
        html: this.isHtmlContent(text) ? text : this.markdownToHtml(text)
      };
    },
    isHtmlContent(value) {
      return /<\/?[a-z][\s\S]*>/i.test(String(value || ''));
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    formatInlineMarkdown(value) {
      const escaped = this.escapeHtml(value);
      return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/__(.+?)__/g, '<strong>$1</strong>');
    },
    closeLists(htmlParts, listStack, targetIndent = -1) {
      while (listStack.length && listStack[listStack.length - 1].indent > targetIndent) {
        const list = listStack.pop();
        htmlParts.push(`</${list.type}>`);
      }
    },
    openListIfNeeded(htmlParts, listStack, indent, type) {
      const top = listStack[listStack.length - 1];
      if (!top || indent > top.indent || top.type !== type) {
        htmlParts.push(`<${type}>`);
        listStack.push({ indent, type });
      }
    },
    markdownToHtml(source) {
      const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
      const htmlParts = [];
      const listStack = [];

      for (let i = 0; i < lines.length; i += 1) {
        const rawLine = lines[i].replace(/\t/g, '    ');
        const indent = (rawLine.match(/^ */) || [''])[0].length;
        const content = rawLine.trim();

        if (!content) {
          this.closeLists(htmlParts, listStack, -1);
          continue;
        }

        const orderedMatch = content.match(/^(\d+)\.\s+(.*)$/);
        const unorderedMatch = content.match(/^[-*]\s+(.*)$/);

        if (orderedMatch || unorderedMatch) {
          const type = orderedMatch ? 'ol' : 'ul';
          const itemText = orderedMatch ? orderedMatch[2] : unorderedMatch[1];

          while (listStack.length && indent < listStack[listStack.length - 1].indent) {
            const list = listStack.pop();
            htmlParts.push(`</${list.type}>`);
          }

          if (
            listStack.length &&
            indent === listStack[listStack.length - 1].indent &&
            type !== listStack[listStack.length - 1].type
          ) {
            const list = listStack.pop();
            htmlParts.push(`</${list.type}>`);
          }

          this.openListIfNeeded(htmlParts, listStack, indent, type);
          htmlParts.push(`<li>${this.formatInlineMarkdown(itemText)}</li>`);
          continue;
        }

        this.closeLists(htmlParts, listStack, -1);

        const headingMatch = content.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
          const level = Math.min(6, headingMatch[1].length);
          htmlParts.push(`<h${level}>${this.formatInlineMarkdown(headingMatch[2])}</h${level}>`);
          continue;
        }

        htmlParts.push(`<p>${this.formatInlineMarkdown(content)}</p>`);
      }

      this.closeLists(htmlParts, listStack, -1);
      return htmlParts.join('');
    },
    getReportText(info) {
      if (typeof info === 'string') {
        return info.trim();
      }
      if (!info || typeof info !== 'object') {
        return '';
      }
      const candidateList = [
        info?.data,
        info?.report,
        info?.content,
        info?.info?.report,
        info?.info?.content,
        info?.detail?.report,
        info?.detail?.content
      ];
      for (let i = 0; i < candidateList.length; i += 1) {
        const current = candidateList[i];
        if (typeof current === 'string' && current.trim()) {
          return current.trim();
        }
      }
      return '';
    },
    async fetchMeetingReport() {
      const meetingId = this.getMeetingId();
      if (!meetingId) {
        this.reportData = null;
        return;
      }
      this.isLoading = true;
      try {
        const res = await getMeetingReport({
          meetingId
        });
        this.reportData = this.normalizeReport(res);
      } catch (error) {
        this.reportData = null;
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.meeting-report-page {
  min-height: 100vh;
  background: #ffffff;
}

.content {
  padding: 3.58974vw;
}

.state-text {
  padding: 10.25641vw 0;
  text-align: center;
  color: #999;
  font-size: 3.84615vw;
}

.report-card {
  background: #ffffff;
}

.report-title {
  color: #3d2a2a;
  font-size: 4.10256vw;
  font-weight: 700;
}

.report-text {
  margin-top: 2.05128vw;
  color: #4a3434;
  font-size: 3.58974vw;
  line-height: 1.7;
  word-break: break-word;
}

.report-text :deep(p) {
  margin: 0 0 2.05128vw;
}

.report-text :deep(h1),
.report-text :deep(h2),
.report-text :deep(h3),
.report-text :deep(h4),
.report-text :deep(h5),
.report-text :deep(h6) {
  margin: 3.07692vw 0 2.05128vw;
  color: #3d2a2a;
  font-size: 3.84615vw;
  font-weight: 700;
  line-height: 1.5;
}

.report-text :deep(ol),
.report-text :deep(ul) {
  margin: 0 0 2.05128vw;
  padding-left: 4.87179vw;
}

.report-text :deep(li) {
  margin-bottom: 1.02564vw;
}

.report-text :deep(strong) {
  color: #3d2a2a;
  font-weight: 700;
}
</style>
